import { useState } from 'react';
import { BookOpen, Key, Question, XCircle } from '@phosphor-icons/react';
import Modal from '../../../../components/molecules/Modal/Modal';
import { LinkingHelpVideoOption } from '../LinkingHelpVideo';
import PrimaryButton from '../../../../components/atoms/Button';
import LinkingOptionButton from '../LinkingOptionButton';
import PasswordRecoveryContent from '../PasswordRecoveryContent';
import { mapLinkingError } from '../mapLinkingError';
import { FRONTAPP_ARTICLES } from '../../../../shared/constants/articles';
import type {
	HelpModalCloseReason,
	HelpModalOption,
} from '../orchestration/useLinkingTracking';

const ModalFailed = ({
	open,
	failedInfo,
	handleCloseModal,
	onOptionClick,
	linkingOptionSelected,
	setLinkingOptionSelected,
	setStep,
	setSelectedSite,
}: {
	open: boolean;
	failedInfo: string;
	handleCloseModal: (reason: HelpModalCloseReason) => void;
	/** Cada fila del modal avisa antes de ejecutarse: es el numerador del embudo
	 * de ayuda (abrió → tocó qué). */
	onOptionClick: (option: HelpModalOption) => void;
	linkingOptionSelected: 'link' | 'efirma';
	setLinkingOptionSelected: (option: 'link' | 'efirma') => void;
	setStep: (step: number) => void;
	setSelectedSite: (site: string | null) => void;
}) => {
	// Mientras el video está encima, este modal se tapa sin cerrarse: cerrarlo
	// perdería el estado y dejarlo visible enseña dos paneles encimados.
	const [videoOpen, setVideoOpen] = useState(false);

	// La misma fila del video en los dos métodos: primera, arriba del artículo.
	// Emite `help_modal_option_clicked option=video` y `linking_help_video
	// source=help_modal`, con `type` del método que esté abierto.
	const videoOption = (
		<LinkingHelpVideoOption
			onOpenChange={(videoIsOpen) => {
				setVideoOpen(videoIsOpen);
				if (videoIsOpen) onOptionClick('video');
			}}
		/>
	);

	// Encabezado: badge + título + causa. El badge da el tono (rojo = falló algo,
	// azul = solo estás preguntando) sin teñir el texto entero de rojo, y la
	// causa concreta va como subtítulo separado del título genérico.
	const getHeader = () => {
		if (failedInfo) {
			return (
				<div className='flex items-start gap-3 pr-6'>
					<span className='bg-h-error-90 text-h-error-50 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl'>
						<XCircle size={24} weight='fill' />
					</span>
					<div className='min-w-0'>
						<h2 className='text-h-text-heading text-lg leading-tight font-semibold'>
							No pudimos conectar tu cuenta
						</h2>
						<p className='text-h-error-50 mt-1 text-sm leading-snug font-medium'>
							{mapLinkingError(failedInfo)}
						</p>
					</div>
				</div>
			);
		}

		return (
			<div className='flex items-start gap-3 pr-6'>
				<span className='bg-h-primary-95 text-h-primary-50 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl'>
					<Question size={24} weight='fill' />
				</span>
				<h2 className='text-h-text-heading text-lg leading-tight font-semibold'>
					{linkingOptionSelected === 'link'
						? '¿Cómo recupero mi contraseña del SAT?'
						: '¿Cómo recupero mis archivos de la e.firma?'}
				</h2>
			</div>
		);
	};

	const getOptions = () => {
		if (linkingOptionSelected === 'link') {
			return (
				<PasswordRecoveryContent
					onOpenSite={setSelectedSite}
					onOptionClick={onOptionClick}
					leadingOption={videoOption}
					onSwitchToEfirma={() => {
						setLinkingOptionSelected('efirma');
						setStep(2);
						setSelectedSite(null);
						handleCloseModal('option');
					}}
				/>
			);
		}

		// E.Firma
		return (
			<div className='flex flex-col gap-2.5'>
				{videoOption}
				<LinkingOptionButton
					icon={<BookOpen size={20} weight='fill' />}
					text='Si necesitas ayuda te compartimos un <b class="font-semibold">artículo que te ayudará</b> a conectar tu cuenta del SAT.'
					action={() => {
						onOptionClick('article');
						setSelectedSite(FRONTAPP_ARTICLES.efirma);
					}}
				/>
				<LinkingOptionButton
					icon={<Key size={20} weight='fill' />}
					text='Recuerda que puedes vincular por <b class="font-semibold">RFC y contraseña</b> también.'
					action={() => {
						onOptionClick('switch_method');
						setLinkingOptionSelected('link');
						setStep(2);
						setSelectedSite(null);
						handleCloseModal('option');
					}}
				/>
			</div>
		);
	};

	return (
		<Modal
			open={open}
			onClose={() => handleCloseModal('dismiss')}
			hidden={videoOpen}
			sx='!p-4 md:!p-5 w-[96%] max-w-lg'
		>
			<div className='flex flex-col gap-5'>
				{getHeader()}
				{getOptions()}

				<PrimaryButton
					label='Cerrar'
					sx='mt-1'
					onClick={() => handleCloseModal('button')}
				/>
			</div>
		</Modal>
	);
};

export default ModalFailed;
